import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import './AdminNews.css';
import api from '../../../services/api';
import { emitToast } from '../../../utils/toast';
import {
  Archive,
  Check,
  Eye,
  Film,
  Folder,
  Heart,
  MessageSquare,
  Megaphone,
  Pause,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';

const AdminNews = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [activeTab, setActiveTab] = useState('articles'); // articles|media|comments|announcements
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [editing, setEditing] = useState(null); // { id, title, content, status }
  const [previewPost, setPreviewPost] = useState(null);
  const [totalArticles, setTotalArticles] = useState(0);
  const [publishedTotal, setPublishedTotal] = useState(null);
  const [draftTotal, setDraftTotal] = useState(null);
  const [mediaList, setMediaList] = useState([]);
  const [mediaTotal, setMediaTotal] = useState(0);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all'); // all|image|video|document (doc not used yet)
  const [mediaSearch, setMediaSearch] = useState('');
  const [viewerMedia, setViewerMedia] = useState(null); // {type, url, title}
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'general' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadNotice, setUploadNotice] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentsList, setCommentsList] = useState([]);
  const [commentsFilter, setCommentsFilter] = useState('all'); // all|approved|pending|rejected
  const [commentsTotal, setCommentsTotal] = useState(0);
  // Annonces importantes (admin)
  const [annList, setAnnList] = useState([]);
  const [annLoading, setAnnLoading] = useState(false);
  const [annError, setAnnError] = useState('');
  const [annForm, setAnnForm] = useState({ title: '', description: '', buttonText: '', link: '', status: 'active', startsAt: '', endsAt: '' });
  const [annEditing, setAnnEditing] = useState(null); // { id, ...fields }
  const [annModalOpen, setAnnModalOpen] = useState(false);
  const [annActiveCount, setAnnActiveCount] = useState(0);
  const [annInactiveCount, setAnnInactiveCount] = useState(0);
  const fileInputRef = useRef(null);
  const API_BASE = (api.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');
  // Quand on clique "Choisir dans la bibliothèque" depuis un formulaire, on marque la cible
  // { mode: 'create' } ou { mode: 'edit', id }
  const [chooseCoverFor, setChooseCoverFor] = useState(null);
  // Modal sélecteur de média (au-dessus des autres modales)
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const pickerFileRef = useRef(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('Confirmer');
  const [confirmMessage, setConfirmMessage] = useState('');
  const confirmActionRef = useRef(null);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyPostId, setReplyPostId] = useState(null);
  const [replyText, setReplyText] = useState('');

  const handleOverlayMouseDown = (e, onClose) => {
    if (e.target !== e.currentTarget) return;
    onClose();
  };

  const openConfirm = ({ title = 'Confirmer', message = '', onConfirm }) => {
    confirmActionRef.current = typeof onConfirm === 'function' ? onConfirm : null;
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmTitle('Confirmer');
    setConfirmMessage('');
    confirmActionRef.current = null;
  };

  const handleConfirmSubmit = async () => {
    const fn = confirmActionRef.current;
    closeConfirm();
    if (!fn) return;
    try {
      await fn();
    } catch (e) {
      emitToast('Action impossible.');
    }
  };

  const openReplyModal = (postId) => {
    setReplyPostId(postId);
    setReplyText('');
    setReplyOpen(true);
  };

  const closeReplyModal = () => {
    setReplyOpen(false);
    setReplyPostId(null);
    setReplyText('');
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;

      if (confirmOpen) {
        closeConfirm();
        return;
      }
      if (replyOpen) {
        closeReplyModal();
        return;
      }

      if (previewPost) {
        setPreviewPost(null);
        return;
      }
      if (viewerMedia) {
        setViewerMedia(null);
        return;
      }
      if (showMediaPicker) {
        setShowMediaPicker(false);
        setChooseCoverFor(null);
        return;
      }
      if (annModalOpen) {
        setAnnModalOpen(false);
        return;
      }
      if (editing) {
        setEditing(null);
        return;
      }
      if (showAddModal) {
        setShowAddModal(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [annModalOpen, confirmOpen, editing, previewPost, replyOpen, showAddModal, showMediaPicker, viewerMedia]);

  const isArticlesTab = activeTab === 'articles';
  const isMediaTab = activeTab === 'media';
  const isCommentsTab = activeTab === 'comments';
  const isAnnouncementsTab = activeTab === 'announcements';

  // Appliquer l'URL donnée en tant que couverture, selon la cible (create/edit)
  const applyMediaAsCover = (url) => {
    if (chooseCoverFor?.mode === 'create') {
      setNewPost(prev => ({ ...prev, coverUrl: url }));
    } else if (chooseCoverFor?.mode === 'edit') {
      setEditing(prev => prev ? ({ ...prev, coverUrl: url }) : prev);
    }
    setChooseCoverFor(null);
    setShowMediaPicker(false);
  };

  // Upload direct depuis le PC dans le picker, puis utiliser comme couverture
  const handlePickerFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append('media', file);
      form.append('title', file.name);
      const res = await api.post('/api/media', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const created = res?.data?.media;
      if (created?.url) {
        // On utilise immédiatement le média comme couverture (qu'il soit pending ou pas)
        applyMediaAsCover(created.url);
      } else {
        emitToast("Upload terminé, mais le média n'a pas pu être utilisé.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Échec de l'upload.";
      emitToast(msg);
    } finally {
      e.target.value = '';
    }
  };

  // Compteurs de posts par statut
  const fetchPostCounts = useCallback(async () => {
    try {
      const [pubRes, draftRes] = await Promise.all([
        api.get('/api/posts?status=published&limit=1&page=1'),
        api.get('/api/posts?status=draft&limit=1&page=1')
      ]);
      setPublishedTotal(typeof pubRes?.data?.total === 'number' ? pubRes.data.total : null);
      setDraftTotal(typeof draftRes?.data?.total === 'number' ? draftRes.data.total : null);
    } catch (e) {
      setPublishedTotal(null);
      setDraftTotal(null);
    }
  }, []);

  // Charger les posts depuis l'API
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchQuery.trim() !== '') params.set('search', searchQuery.trim());
      params.set('page', String(currentPage));
      params.set('limit', String(pageSize));
      const res = await api.get(`/api/posts?${params.toString()}`);
      const payload = res?.data;
      const data = Array.isArray(payload?.posts) ? payload.posts : (Array.isArray(payload) ? payload : []);
      setPosts(data);
      if (payload?.totalPages) setTotalPages(Number(payload.totalPages));
      if (typeof payload?.total === 'number') setTotalArticles(Number(payload.total));
      // rafraîchir les compteurs publiés/brouillons
      fetchPostCounts();
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [currentPage, fetchPostCounts, pageSize, searchQuery, statusFilter]);

  // Médias: liste + upload
  const fetchMedia = useCallback(async () => {
    try {
      setMediaLoading(true);
      setMediaError('');
      const qs = new URLSearchParams();
      qs.set('status', 'all');
      qs.set('page', '1');
      qs.set('limit', '50');
      if (mediaTypeFilter !== 'all') qs.set('type', mediaTypeFilter);
      const res = await api.get(`/api/media?${qs.toString()}`);
      const payload = res?.data;
      const list = Array.isArray(payload?.media) ? payload.media : (Array.isArray(payload) ? payload : []);
      setMediaList(list);
      setMediaTotal(Number(payload?.total || list.length || 0));
    } catch (e) {
      setMediaError("Impossible de charger les médias.");
      setMediaList([]);
      setMediaTotal(0);
    } finally {
      setMediaLoading(false);
    }
  }, [mediaTypeFilter]);

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setPendingUploadFile(file);
    setUploadForm((prev) => ({
      ...prev,
      title: prev.title || file.name,
    }));
    setUploadNotice('');
    setUploadProgress(0);
    e.target.value = '';
  };

  const submitPendingUpload = async () => {
    if (!pendingUploadFile) return;
    try {
      setUploading(true);
      setUploadNotice('');
      setUploadProgress(0);

      const form = new FormData();
      form.append('media', pendingUploadFile);
      form.append('title', uploadForm.title || pendingUploadFile.name);
      form.append('description', uploadForm.description || '');
      form.append('category', uploadForm.category || 'general');

      await api.post('/api/media', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          const total = evt?.total;
          const loaded = evt?.loaded;
          if (typeof total === 'number' && total > 0 && typeof loaded === 'number') {
            const pct = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
            setUploadProgress(pct);
          }
        }
      });

      setUploadNotice('Import terminé. Le média apparaît dans la bibliothèque (statut en attente si modération).');
      setPendingUploadFile(null);
      setUploadForm({ title: '', description: '', category: 'general' });
      setUploadProgress(100);
      await fetchMedia();
    } catch (err) {
      const msg = err?.response?.data?.message || "Échec de l'import.";
      setUploadNotice(msg);
    } finally {
      setUploading(false);
    }
  };

  // Comments API
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      setCommentsError('');
      const url = commentsFilter === 'all' ? '/api/posts/comments' : `/api/posts/comments?status=${commentsFilter}`;
      const res = await api.get(url);
      const payload = res?.data;
      setCommentsList(Array.isArray(payload?.comments) ? payload.comments : []);
      setCommentsTotal(Number(payload?.total || 0));
    } catch (e) {
      setCommentsError('Impossible de charger les commentaires.');
    } finally {
      setCommentsLoading(false);
    }
  }, [commentsFilter]);

  // ====== Annonces Importantes (Admin) ======
  const fetchAnnouncements = useCallback(async () => {
    try {
      setAnnLoading(true);
      setAnnError('');
      const res = await api.get('/api/announcements/all');
      const list = Array.isArray(res.data) ? res.data : [];
      setAnnList(list);
    } catch (e) {
      setAnnError("Impossible de charger les annonces importantes.");
      setAnnList([]);
    } finally {
      setAnnLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // lorsque la recherche change, on repart page 1 et on refetch
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      fetchPosts();
    }, 300);
    return () => clearTimeout(t);
  }, [fetchPosts, searchQuery]);

  // Charger les médias au montage et à l'ouverture de la lib
  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);
  useEffect(() => {
    if (isMediaTab) fetchMedia();
  }, [fetchMedia, isMediaTab]);
  // Rafraîchir quand le type change
  useEffect(() => {
    if (isMediaTab) fetchMedia();
  }, [fetchMedia, mediaTypeFilter, isMediaTab]);
  useEffect(() => {
    if (isCommentsTab) fetchComments();
  }, [fetchComments, isCommentsTab]);
  // Charger les annonces importantes quand la section est ouverte
  useEffect(() => {
    if (isAnnouncementsTab) fetchAnnouncements();
  }, [fetchAnnouncements, isAnnouncementsTab]);

  // Compteur temps réel des annonces actives/inactives
  useEffect(() => {
    let cancelled = false;
    const fetchActiveCount = async () => {
      try {
        const res = await api.get('/api/announcements');
        if (!cancelled) setAnnActiveCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch {
        if (!cancelled) setAnnActiveCount(0);
      }
    };
    const fetchInactiveCount = async () => {
      try {
        const res = await api.get('/api/announcements/all?status=inactive');
        if (!cancelled) setAnnInactiveCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch {
        if (!cancelled) setAnnInactiveCount(0);
      }
    };
    fetchActiveCount();
    fetchInactiveCount();
    const t1 = setInterval(fetchActiveCount, 30000);
    const t2 = setInterval(fetchInactiveCount, 45000);
    return () => { cancelled = true; clearInterval(t1); clearInterval(t2); };
  }, []);
  // Ouvrir le picker déclenchera aussi un rafraîchissement des médias
  useEffect(() => {
    if (showMediaPicker) fetchMedia();
  }, [fetchMedia, showMediaPicker]);

  const handleCloseAdd = () => setShowAddModal(false);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/posts', {
        title: newPost.title,
        content: newPost.content,
        coverUrl: newPost.coverUrl || ''
      });
      setNewPost({ title: '', content: '' });
      setShowAddModal(false);
      fetchPosts();
    } catch (err) {
      emitToast("Impossible de créer l'article. Vérifiez vos droits et réessayez.");
    }
  };

  const handleOpenEdit = (p) => {
    setEditing({ id: p._id, title: p.title, content: p.content, status: p.status, coverUrl: p.coverUrl || '' });
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/api/posts/${editing.id}`, { title: editing.title, content: editing.content, coverUrl: editing.coverUrl || '' });
      setEditing(null);
      fetchPosts();
    } catch (err) {
      emitToast("Échec de la modification. Vérifiez vos droits.");
    }
  };

  const handleModerateComment = async (commentId, status) => {
    try {
      await api.put(`/api/posts/comments/${commentId}/moderate`, { status });
      fetchComments();
    } catch (e) {
      emitToast('Action impossible. Vérifiez vos droits (admin).');
    }
  };

  const handleDeleteComment = async (commentId) => {
    openConfirm({
      title: 'Supprimer le commentaire',
      message: 'Cette action est définitive. Voulez-vous continuer ?',
      onConfirm: async () => {
        await api.delete(`/api/posts/comments/${commentId}`);
        fetchComments();
      }
    });
  };

  const handleReplyToPost = async (postId) => {
    openReplyModal(postId);
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    const content = replyText.trim();
    if (!replyPostId || !content) return;
    try {
      await api.post(`/api/posts/${replyPostId}/comments`, { content });
      emitToast('Réponse publiée');
      closeReplyModal();
      if (isCommentsTab) fetchComments();
    } catch (err) {
      const msg = err?.response?.data?.message || "Impossible de répondre (connexion requise).";
      emitToast(msg);
    }
  };

  // Media moderation (approve/reject)
  const handleModerateMediaStatus = async (mediaId, status) => {
    try {
      await api.put(`/api/media/${mediaId}/moderate`, { status });
      await fetchMedia();
    } catch (e) {
      emitToast('Action impossible. Rôle admin/moderator requis.');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    openConfirm({
      title: 'Supprimer le média',
      message: 'Cette action est définitive. Voulez-vous continuer ?',
      onConfirm: async () => {
        await api.delete(`/api/media/${mediaId}`);
        await fetchMedia();
      }
    });
  };

  const requestDeletePost = (postId) => {
    openConfirm({
      title: 'Supprimer l\'article',
      message: 'Cette action est définitive. Voulez-vous continuer ?',
      onConfirm: async () => {
        await api.delete(`/api/posts/${postId}`);
        fetchPosts();
      }
    });
  };

  const toggleStatus = async (p) => {
    try {
      const next = p.status === 'published' ? 'draft' : 'published';
      await api.put(`/api/posts/${p._id}/status`, { status: next });
      fetchPosts();
    } catch (err) {
      emitToast("Impossible de changer le statut. Vérifiez vos droits.");
    }
  };

  const renderMediaLibrary = () => (
    <div className="media-library">
      <div className="media-header">
        <h3>Bibliothèque de médias</h3>
        <div className="media-actions">
          <button className="upload-btn" type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
            <Plus size={18} aria-hidden="true" />
            <span>Importer des médias</span>
          </button>
          <button className="organize-btn" onClick={() => fetchMedia()}>Organiser</button>
          <input type="file" ref={fileInputRef} className="file-input-hidden" accept="image/*,video/*" onChange={handleFileSelected} />
        </div>
      </div>

      {(pendingUploadFile || uploading || uploadNotice) && (
        <div className="media-upload-panel" role="region" aria-label="Importer un média">
          <div className="media-upload-panel__row">
            <div className="media-upload-file">
              <div className="media-upload-file__label">Fichier</div>
              <div className="media-upload-file__name">{pendingUploadFile ? pendingUploadFile.name : '—'}</div>
              {pendingUploadFile && (
                <button type="button" className="media-upload-cancel" onClick={() => { if (!uploading) { setPendingUploadFile(null); setUploadNotice(''); setUploadProgress(0); } }} disabled={uploading}>
                  Annuler
                </button>
              )}
            </div>

            <div className="media-upload-fields">
              <input
                className="media-upload-input"
                type="text"
                placeholder="Titre (ex: Tournoi interquartiers)"
                value={uploadForm.title}
                onChange={(e) => setUploadForm((p) => ({ ...p, title: e.target.value }))}
                disabled={uploading}
              />
              <textarea
                className="media-upload-textarea"
                placeholder="Description (1-2 phrases pour raconter le moment)"
                value={uploadForm.description}
                onChange={(e) => setUploadForm((p) => ({ ...p, description: e.target.value }))}
                disabled={uploading}
              />
              <select
                className="media-upload-select"
                value={uploadForm.category}
                onChange={(e) => setUploadForm((p) => ({ ...p, category: e.target.value }))}
                disabled={uploading}
              >
                <option value="general">Vie du quartier</option>
                <option value="event">Événements</option>
                <option value="project">Projets</option>
                <option value="history">Histoire</option>
              </select>
              <button
                type="button"
                className="media-upload-submit"
                onClick={submitPendingUpload}
                disabled={uploading || !pendingUploadFile}
              >
                {uploading ? `Import… ${uploadProgress}%` : 'Importer maintenant'}
              </button>
            </div>
          </div>

          {uploading && (
            <div className="media-upload-progress" aria-label="Progression">
              <div className="media-upload-progress__bar" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}

          {uploadNotice && <div className="media-upload-notice">{uploadNotice}</div>}
        </div>
      )}

      <div className="media-filters">
        <select className="media-type-filter" value={mediaTypeFilter} onChange={(e) => setMediaTypeFilter(e.target.value)}>
          <option value="all">Tous les types</option>
          <option value="image">Images</option>
          <option value="video">Vidéos</option>
        </select>
        <input type="text" placeholder="Rechercher un média..." className="media-search" value={mediaSearch} onChange={(e) => setMediaSearch(e.target.value)} />
      </div>
      <div className="media-grid">
        {mediaLoading && <div className="news-page__empty">Chargement des médias…</div>}
        {!mediaLoading && mediaError && <div className="media-error">{mediaError}</div>}
        {!mediaLoading && !mediaError && mediaList.length === 0 && <div className="news-page__empty">Aucun média</div>}
        {!mediaLoading && !mediaError && mediaList
          .filter(m => mediaTypeFilter === 'all' || m.type === mediaTypeFilter)
          .filter(m => {
            const q = mediaSearch.trim().toLowerCase();
            if (!q) return true;
            const name = (m.title || m.name || '').toLowerCase();
            return name.includes(q);
          })
          .map((media) => (
          <div key={media._id} className="media-item">
            <button className="media-preview" type="button" onClick={() => setViewerMedia(media)} title="Aperçu" aria-label="Prévisualiser">
              {media.type === 'image' ? (
                <img src={toAbsoluteMediaUrl(media.url)} alt={media.title || media.name || 'media'} />
              ) : (
                <div className="video-preview" aria-label="Vidéo">
                  <Film size={22} aria-hidden="true" />
                </div>
              )}
            </button>
            <div className="media-info">
              <span className="media-name">{media.title || media.name || '—'}</span>
              <span className="media-size">{media.metadata?.size || ''}</span>
              <span className={`media-status status-${media.status || 'pending'}`}>{media.status || 'pending'}</span>
            </div>
            <div className="media-item-actions" aria-label="Actions média">
              <button className="media-action-btn" type="button" onClick={() => setViewerMedia(media)}>
                <Eye size={16} aria-hidden="true" />
                Prévisualiser
              </button>
              {media.status !== 'approved' && (
                <button className="media-action-btn is-approve" type="button" onClick={() => handleModerateMediaStatus(media._id, 'approved')}>
                  <Check size={16} aria-hidden="true" />
                  Approuver
                </button>
              )}
              {media.status !== 'rejected' && (
                <button className="media-action-btn is-reject" type="button" onClick={() => handleModerateMediaStatus(media._id, 'rejected')}>
                  <Archive size={16} aria-hidden="true" />
                  Rejeter
                </button>
              )}
              <button className="media-action-btn is-danger" type="button" onClick={() => handleDeleteMedia(media._id)}>
                <Trash2 size={16} aria-hidden="true" />
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const openCreateAnn = () => {
    setAnnForm({ title: '', description: '', buttonText: '', link: '', status: 'active', startsAt: '', endsAt: '' });
    setAnnEditing(null);
    setAnnModalOpen(true);
  };

  const openEditAnn = (a) => {
    setAnnEditing(a);
    setAnnForm({
      title: a.title || '',
      description: a.description || '',
      buttonText: a.buttonText || '',
      link: a.link || '',
      status: a.status || 'inactive',
      startsAt: a.startsAt ? String(a.startsAt).slice(0, 16) : '',
      endsAt: a.endsAt ? String(a.endsAt).slice(0, 16) : '',
    });
    setAnnModalOpen(true);
  };

  const saveAnnouncement = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: annForm.title,
        description: annForm.description,
        buttonText: annForm.buttonText,
        link: annForm.link,
        status: annForm.status,
        startsAt: annForm.startsAt ? new Date(annForm.startsAt).toISOString() : undefined,
        endsAt: annForm.endsAt ? new Date(annForm.endsAt).toISOString() : undefined,
      };
      if (annEditing?.id) {
        await api.put(`/api/announcements/${annEditing.id}`, payload);
      } else {
        await api.post('/api/announcements', payload);
      }
      setAnnModalOpen(false);
      await fetchAnnouncements();
      // refresh compteurs
      try {
        const [r1, r2] = await Promise.all([
          api.get('/api/announcements'),
          api.get('/api/announcements/all?status=inactive')
        ]);
        setAnnActiveCount(Array.isArray(r1.data)?r1.data.length:0);
        setAnnInactiveCount(Array.isArray(r2.data)?r2.data.length:0);
      } catch {}
    } catch (err) {
      emitToast("Enregistrement impossible. Vérifiez vos droits.");
    }
  };

  const toggleAnnStatus = async (a) => {
    try {
      const next = a.status === 'active' ? 'inactive' : 'active';
      await api.put(`/api/announcements/${a.id}`, { status: next });
      await fetchAnnouncements();
      try {
        const [r1, r2] = await Promise.all([
          api.get('/api/announcements'),
          api.get('/api/announcements/all?status=inactive')
        ]);
        setAnnActiveCount(Array.isArray(r1.data)?r1.data.length:0);
        setAnnInactiveCount(Array.isArray(r2.data)?r2.data.length:0);
      } catch {}
    } catch {
      emitToast("Changement de statut impossible");
    }
  };

  const deleteAnn = async (a) => {
    openConfirm({
      title: 'Supprimer l\'annonce',
      message: 'Cette action est définitive. Voulez-vous continuer ?',
      onConfirm: async () => {
        await api.delete(`/api/announcements/${a.id}`);
        await fetchAnnouncements();
        try {
          const r = await api.get('/api/announcements');
          setAnnActiveCount(Array.isArray(r.data) ? r.data.length : 0);
        } catch {}
      }
    });
  };

  const renderAnnouncementsSection = () => (
    <div className="announcements-admin">
      <div className="ann-header">
        <h3>Annonces Importantes</h3>
        <button className="add-news-btn" type="button" onClick={openCreateAnn}>
          <Plus size={18} aria-hidden="true" />
          <span>Nouvelle annonce</span>
        </button>
      </div>
      {annLoading && <div className="news-page__empty">Chargement…</div>}
      {!annLoading && annError && <div className="media-error">{annError}</div>}
      {!annLoading && !annError && annList.length === 0 && <div className="news-page__empty">Aucune annonce</div>}
      {!annLoading && !annError && annList.length > 0 && (
        <>
        <div className="articles-table announcements-table--desktop">
          <table>
            <thead>
              <tr>
                <th>Titre</th>
                <th>Statut</th>
                <th>Début</th>
                <th>Fin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {annList.map(a => (
                <tr key={a.id}>
                  <td>{a.title}</td>
                  <td><span className={`status-badge ${a.status}`}>{a.status === 'active' ? 'Active' : 'Inactive'}</span></td>
                  <td>{a.startsAt ? new Date(a.startsAt).toLocaleString('fr-FR') : '—'}</td>
                  <td>{a.endsAt ? new Date(a.endsAt).toLocaleString('fr-FR') : '—'}</td>
                  <td className="actions-cell">
                    <button className="action-btn edit" type="button" title="Modifier" aria-label="Modifier" onClick={() => openEditAnn(a)}>
                      <Pencil size={16} aria-hidden="true" />
                    </button>
                    <button
                      className="action-btn"
                      type="button"
                      title={a.status==='active'?'Désactiver':'Activer'}
                      aria-label={a.status==='active'?'Désactiver':'Activer'}
                      onClick={() => toggleAnnStatus(a)}
                    >
                      {a.status==='active' ? <Pause size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
                    </button>
                    <button className="action-btn delete" type="button" title="Supprimer" aria-label="Supprimer" onClick={() => deleteAnn(a)}>
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="news-mobile-cards announcements-cards--mobile" role="list" aria-label="Annonces importantes">
          {annList.map((a) => (
            <div className="news-mobile-card" key={a.id} role="listitem">
              <div className="news-mobile-card__top">
                <div className="news-mobile-card__title">{a.title}</div>
                <div className="news-mobile-card__meta">
                  <span className={`status-badge ${a.status}`}>{a.status === 'active' ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <div className="news-mobile-card__grid">
                <div className="news-mobile-card__field">
                  <div className="news-mobile-card__label">Début</div>
                  <div className="news-mobile-card__value">{a.startsAt ? new Date(a.startsAt).toLocaleString('fr-FR') : '—'}</div>
                </div>
                <div className="news-mobile-card__field">
                  <div className="news-mobile-card__label">Fin</div>
                  <div className="news-mobile-card__value">{a.endsAt ? new Date(a.endsAt).toLocaleString('fr-FR') : '—'}</div>
                </div>
              </div>
              <div className="news-mobile-card__actions" aria-label="Actions">
                <button className="media-action-btn" type="button" onClick={() => openEditAnn(a)}>
                  <Pencil size={16} aria-hidden="true" />
                  Modifier
                </button>
                <button
                  className={a.status === 'active' ? 'media-action-btn is-reject' : 'media-action-btn is-approve'}
                  type="button"
                  onClick={() => toggleAnnStatus(a)}
                >
                  {a.status === 'active' ? (
                    <>
                      <Pause size={16} aria-hidden="true" />
                      Désactiver
                    </>
                  ) : (
                    <>
                      <Check size={16} aria-hidden="true" />
                      Activer
                    </>
                  )}
                </button>
                <button className="media-action-btn is-danger" type="button" onClick={() => deleteAnn(a)}>
                  <Trash2 size={16} aria-hidden="true" />
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {annModalOpen && (
        <div className="modal-overlay" onMouseDown={(e) => handleOverlayMouseDown(e, () => setAnnModalOpen(false))}>
          <div className="modal">
            <h3>{annEditing? 'Modifier l\'annonce' : 'Créer une annonce'}</h3>
            <form onSubmit={saveAnnouncement}>
              <div className="form-row">
                <label>Titre</label>
                <input type="text" value={annForm.title} onChange={(e) => setAnnForm((p) => ({ ...p, title: e.target.value }))} required />
              </div>
              <div className="form-row">
                <label>Description</label>
                <textarea rows="4" value={annForm.description} onChange={(e) => setAnnForm((p) => ({ ...p, description: e.target.value }))} required />
              </div>
              <div className="form-row">
                <label>Texte du bouton</label>
                <input type="text" value={annForm.buttonText} onChange={(e) => setAnnForm((p) => ({ ...p, buttonText: e.target.value }))} />
              </div>
              <div className="form-row">
                <label>Lien (URL)</label>
                <input type="url" value={annForm.link} onChange={(e) => setAnnForm((p) => ({ ...p, link: e.target.value }))} placeholder="https://…" />
              </div>
              <div className="form-row ann-dates-grid">
                <div className="ann-dates-field">
                  <label>Début (optionnel)</label>
                  <input type="datetime-local" value={annForm.startsAt} onChange={(e) => setAnnForm((p) => ({ ...p, startsAt: e.target.value }))} />
                </div>
                <div className="ann-dates-field">
                  <label>Fin (optionnel)</label>
                  <input type="datetime-local" value={annForm.endsAt} onChange={(e) => setAnnForm((p) => ({ ...p, endsAt: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <label>Statut</label>
                <select value={annForm.status} onChange={(e) => setAnnForm((p) => ({ ...p, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={()=>setAnnModalOpen(false)}>Annuler</button>
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // Heuristique pour déterminer le MIME type vidéo depuis l'extension
  const inferMime = (url = '') => {
    const lower = url.toLowerCase();
    if (lower.endsWith('.mp4')) return 'video/mp4';
    if (lower.endsWith('.webm')) return 'video/webm';
    if (lower.endsWith('.ogg') || lower.endsWith('.ogv')) return 'video/ogg';
    if (lower.endsWith('.mov')) return 'video/quicktime';
    return undefined;
  };

  const toAbsoluteMediaUrl = (url = '') => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    const fixedProto = raw.replace(/^https\//i, 'https://').replace(/^http\//i, 'http://');
    if (/^https?:\/\//i.test(fixedProto)) return fixedProto;
    if (fixedProto.startsWith('/')) return `${API_BASE}${fixedProto}`;
    return `${API_BASE}/${fixedProto}`;
  };

  const ArticlePreviewModal = () => (
    !previewPost ? null : (
      <div className="modal-overlay" onClick={() => setPreviewPost(null)}>
        <div className="modal article-preview-modal" onClick={(e) => e.stopPropagation()}>
          <div className="article-preview-header">
            <div className="article-preview-header__text">
              <h3 className="article-preview-header__title">{previewPost.title || '—'}</h3>
              <div className="article-preview-meta">
                <span>{previewPost.author?.name || '—'}</span>
                <span>•</span>
                <span>{previewPost.createdAt ? new Date(previewPost.createdAt).toLocaleString('fr-FR') : '—'}</span>
              </div>
            </div>
            <button type="button" className="btn-secondary" onClick={() => setPreviewPost(null)}>
              <X size={16} aria-hidden="true" />
              Fermer
            </button>
          </div>

          {previewPost.coverUrl && (
            <div className="article-preview-cover">
              <img src={toAbsoluteMediaUrl(previewPost.coverUrl)} alt={previewPost.title || 'couverture'} />
            </div>
          )}

          <div className="article-preview-content">
            {String(previewPost.content || '').trim() ? previewPost.content : '—'}
          </div>
        </div>
      </div>
    )
  );

  const MediaViewer = () => (
    !viewerMedia ? null : (
      <div className="modal-overlay news-modal-overlay news-modal-overlay--viewer" onClick={() => setViewerMedia(null)}>
        <div className="modal news-modal news-modal--viewer" onClick={(e) => e.stopPropagation()}>
          <div className="news-modal__topbar">
            <button type="button" onClick={() => setViewerMedia(null)} className="btn-secondary news-modal__close">
              <X size={16} aria-hidden="true" />
              Fermer
            </button>
          </div>
          <div className="news-modal__body">
            {viewerMedia.type === 'image' ? (
              <img className="news-modal__image" src={toAbsoluteMediaUrl(viewerMedia.url)} alt={viewerMedia.title || viewerMedia.name || 'media'} />
            ) : (
              <video className="news-modal__video" controls preload="metadata" crossOrigin="anonymous">
                <source src={toAbsoluteMediaUrl(viewerMedia.url)} type={inferMime(viewerMedia.url)} />
              </video>
            )}
          </div>
          <div className="news-modal__footer">{viewerMedia.title || viewerMedia.name || ''}</div>
        </div>
      </div>
    )
  );

  // Media Picker modal (au-dessus des autres modales) pour choisir une couverture
  const MediaPicker = () => (
    !showMediaPicker ? null : (
      <div className="modal-overlay news-modal-overlay news-modal-overlay--picker" onClick={() => { setShowMediaPicker(false); setChooseCoverFor(null); }}>
        <div className="modal news-modal news-modal--picker" onClick={(e) => e.stopPropagation()}>
          <h3>Sélectionner une image de couverture</h3>
          <div className="news-modal__actions">
            <button className="upload-btn" type="button" onClick={() => pickerFileRef.current && pickerFileRef.current.click()}>Téléverser depuis l'ordinateur</button>
            <input type="file" ref={pickerFileRef} className="file-input-hidden" accept="image/*" onChange={handlePickerFileSelected} />
            <div className="news-modal__actions" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div className="pagination__meta">Sélecteur: images de la bibliothèque</div>
              <button className="btn-secondary" type="button" onClick={() => { setShowMediaPicker(false); setChooseCoverFor(null); }}>Fermer</button>
            </div>
          </div>
          <div className="media-grid news-modal__grid">
            {mediaLoading && <div className="news-page__empty">Chargement des médias…</div>}
            {!mediaLoading && mediaError && <div className="media-error">{mediaError}</div>}
            {!mediaLoading && !mediaError && mediaList.filter(m => m.type === 'image').length === 0 && <div className="news-page__empty">Aucune image</div>}
            {!mediaLoading && !mediaError && mediaList.filter(m => m.type === 'image').map((media) => (
              <div key={media._id} className="media-item news-modal__pick-item" onClick={() => applyMediaAsCover(media.url)}>
                <button className="media-preview" type="button" title="Choisir" aria-label="Choisir">
                  <img src={toAbsoluteMediaUrl(media.url)} alt={media.title || media.name || 'media'} />
                </button>
                <div className="media-info">
                  <span className="media-name">{media.title || media.name || '—'}</span>
                  <span className={`media-status status-${media.status || 'pending'}`}>{media.status || 'pending'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  );

  const CommentsSection = () => (
    <div className="comments-section">
      <div className="comments-header">
        <h3>Gestion des commentaires</h3>
        <div className="comments-filters">
          <select className="status-filter" value={commentsFilter} onChange={(e) => setCommentsFilter(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="approved">Approuvés</option>
            <option value="pending">En attente</option>
            <option value="rejected">Rejetés</option>
          </select>
        </div>
      </div>
      <div className="comments-list">
        {commentsLoading && <div className="news-page__empty">Chargement des commentaires…</div>}
        {!commentsLoading && commentsError && <div className="comments-error">{commentsError}</div>}
        {!commentsLoading && !commentsError && commentsList.length === 0 && <div className="news-page__empty">Aucun commentaire</div>}
        {!commentsLoading && !commentsError && commentsList.map((comment) => (
          <div key={comment.id} className="comment-item">
            <div className="comment-header">
              <div>
                <div className="comment-author">{comment.authorName || 'Anonyme'}</div>
              </div>
              <span className="comment-date">{new Date(comment.createdAt).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="comment-content">{comment.content}</div>
            <div className="comment-article">Sur : {comment.postTitle}</div>
            <div className="comment-actions">
              <button className="media-action-btn is-approve" type="button" onClick={() => handleModerateComment(comment.id, 'approved')}>
                <Check size={16} aria-hidden="true" />
                Approuver
              </button>
              <button className="media-action-btn is-reject" type="button" onClick={() => handleModerateComment(comment.id, 'rejected')}>
                <Archive size={16} aria-hidden="true" />
                Rejeter
              </button>
              <button className="media-action-btn" type="button" onClick={() => handleReplyToPost(comment.postId)}>
                <RefreshCw size={16} aria-hidden="true" />
                Répondre
              </button>
              <button className="media-action-btn is-danger" type="button" onClick={() => handleDeleteComment(comment.id)}>
                <Trash2 size={16} aria-hidden="true" />
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AdminLayout title="Gestion des Actualités">
      <div className="news-page">
          <div className="news-header">
            <div className="header-title">
              <h1>Articles</h1>
              <p className="header-subtitle">Gérez vos articles, médias et commentaires</p>
            </div>
            <div className="header-actions" role="tablist" aria-label="Sections actualités">
              <button
                type="button"
                className={isArticlesTab ? 'news-tab is-active' : 'news-tab'}
                onClick={() => setActiveTab('articles')}
                role="tab"
                aria-selected={isArticlesTab}
              >
                <span>Articles</span>
              </button>
              <button
                type="button"
                className={isMediaTab ? 'news-tab is-active' : 'news-tab'}
                onClick={() => setActiveTab('media')}
                role="tab"
                aria-selected={isMediaTab}
              >
                <Folder size={18} aria-hidden="true" />
                <span>Médias</span>
                <span className="count-badge">{mediaTotal}</span>
              </button>
              <button
                type="button"
                className={isCommentsTab ? 'news-tab is-active' : 'news-tab'}
                onClick={() => setActiveTab('comments')}
                role="tab"
                aria-selected={isCommentsTab}
              >
                <MessageSquare size={18} aria-hidden="true" />
                <span>Commentaires</span>
                <span className="count-badge">{commentsTotal || '—'}</span>
              </button>
              <button
                type="button"
                className={isAnnouncementsTab ? 'news-tab is-active' : 'news-tab'}
                onClick={() => setActiveTab('announcements')}
                role="tab"
                aria-selected={isAnnouncementsTab}
              >
                <Megaphone size={18} aria-hidden="true" />
                <span>Annonces</span>
                <span className={`count-badge ${annActiveCount>0?'active':''}`}>{annActiveCount}</span>
                {annInactiveCount>0 && (
                  <span className={`count-badge ${annInactiveCount>0?'inactive':''}`} title="Inactives à modérer">{annInactiveCount}</span>
                )}
              </button>
            </div>
          </div>

          <div className="news-shortcuts" role="region" aria-label="Raccourcis">
            <div className="news-shortcuts__label">Raccourcis</div>
            <div className="news-shortcuts__actions">
              {isArticlesTab && (
                <button className="add-news-btn" type="button" onClick={() => setShowAddModal(true)}>
                  <Plus size={18} aria-hidden="true" />
                  <span>Créer un article</span>
                </button>
              )}
              {isMediaTab && (
                <button className="add-news-btn" type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                  <Plus size={18} aria-hidden="true" />
                  <span>Importer des médias</span>
                </button>
              )}
              {isAnnouncementsTab && (
                <button className="add-news-btn" type="button" onClick={openCreateAnn}>
                  <Plus size={18} aria-hidden="true" />
                  <span>Nouvelle annonce</span>
                </button>
              )}
              {isCommentsTab && (
                <button className="btn-secondary" type="button" onClick={fetchComments}>
                  <RefreshCw size={16} aria-hidden="true" />
                  Rafraîchir
                </button>
              )}
            </div>
          </div>

          {isMediaTab && renderMediaLibrary()}
          {isCommentsTab && <CommentsSection />}
          {isAnnouncementsTab && renderAnnouncementsSection()}
          <MediaPicker />
          <MediaViewer />
          <ArticlePreviewModal />

          {editing && (
            <div className="modal-overlay" onMouseDown={(e) => handleOverlayMouseDown(e, () => setEditing(null))}>
              <div className="modal">
                <h3>Modifier l'article</h3>
                <form onSubmit={handleSubmitEdit}>
                  <div className="form-row">
                    <label>Titre</label>
                    <input
                      type="text"
                      value={editing.title}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <label>Contenu</label>
                    <textarea
                      rows="6"
                      value={editing.content}
                      onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <label>Image de couverture (URL)</label>
                    <div className="cover-url-row">
                      <input
                        type="text"
                        value={editing.coverUrl || ''}
                        onChange={(e) => setEditing({ ...editing, coverUrl: e.target.value })}
                        placeholder="/uploads/monfichier.jpg"
                      />
                      <button className="media-action-btn" type="button" onClick={() => { setChooseCoverFor({ mode: 'edit', id: editing.id }); setActiveTab('media'); setShowMediaPicker(true); }}>
                        <Folder size={16} aria-hidden="true" />
                        Choisir dans la bibliothèque
                      </button>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Annuler</button>
                    <button type="submit" className="btn-primary">Enregistrer</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showAddModal && (
            <div className="modal-overlay" onMouseDown={(e) => handleOverlayMouseDown(e, () => setShowAddModal(false))}>
              <div className="modal">
                <h3>Créer un article</h3>
                <form onSubmit={handleCreatePost}>
                  <div className="form-row">
                    <label>Titre</label>
                    <input
                      type="text"
                      value={newPost.title}
                      onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <label>Contenu</label>
                    <textarea
                      rows="6"
                      value={newPost.content}
                      onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <label>Image de couverture (URL)</label>
                    <div className="cover-url-row">
                      <input
                        type="text"
                        value={newPost.coverUrl || ''}
                        onChange={(e) => setNewPost({ ...newPost, coverUrl: e.target.value })}
                        placeholder="/uploads/monfichier.jpg"
                      />
                      <button className="media-action-btn" type="button" onClick={() => { setChooseCoverFor({ mode: 'create' }); setActiveTab('media'); setShowMediaPicker(true); }}>
                        <Folder size={16} aria-hidden="true" />
                        Choisir dans la bibliothèque
                      </button>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={handleCloseAdd}>Annuler</button>
                    <button type="submit" className="btn-primary">Publier</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {isArticlesTab && (
          <div className="stats-section">
            <div className="stats-grid">
              <div className="stats-card stats-card--overview">
                <h3>Vue d'ensemble</h3>
                <div className="stats-overview">
                  <div className="stat-item">
                    <span className="stat-value">{totalArticles}</span>
                    <span className="stat-label">Articles total</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{publishedTotal ?? '—'}</span>
                    <span className="stat-label">Publiés</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{draftTotal ?? '—'}</span>
                    <span className="stat-label">Brouillons</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">—</span>
                    <span className="stat-label">En attente</span>
                  </div>
                </div>
              </div>

              <div className="stats-card">
                <h3>Engagement</h3>
                <div className="engagement-stats">
                  <div className="stat-item">
                    <span className="stat-icon" aria-hidden="true"><Eye size={18} /></span>
                    <span className="stat-value">—</span>
                    <span className="stat-label">Vues</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-icon" aria-hidden="true"><Heart size={18} /></span>
                    <span className="stat-value">—</span>
                    <span className="stat-label">Likes</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-icon" aria-hidden="true"><MessageSquare size={18} /></span>
                    <span className="stat-value">—</span>
                    <span className="stat-label">Commentaires</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-icon" aria-hidden="true"><RefreshCw size={18} /></span>
                    <span className="stat-value">—</span>
                    <span className="stat-label">Partages</span>
                  </div>
                </div>
              </div>

              <div className="stats-card">
                <h3>Catégories</h3>
                <div className="categories-chart">—</div>
              </div>
            </div>
          </div>
          )}

          {isArticlesTab && (
          <div className="articles-section">
            <div className="articles-header">
              <h2>Liste des Articles</h2>
              <div className="articles-filters">
                <input
                  type="text"
                  placeholder="Rechercher un article..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <div className="filter-group">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="published">Publié</option>
                    <option value="draft">Brouillon</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="articles-table articles-table--desktop">
              <table>
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Auteur</th>
                    <th>Création</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="5" className="table-cell-center news-page__empty">Chargement…</td>
                    </tr>
                  )}
                  {!loading && posts
                    .filter(p =>
                      (statusFilter === 'all' || p.status === statusFilter) &&
                      (searchQuery.trim() === '' || (p.title || '').toLowerCase().includes(searchQuery.toLowerCase()) || (p.content || '').toLowerCase().includes(searchQuery.toLowerCase()))
                    )
                    .map((p) => (
                      <tr key={p._id}>
                        <td>{p.title}</td>
                        <td>{p.author?.name || '—'}</td>
                        <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString('fr-FR') : '—'}</td>
                        <td>
                          <span className={`status-badge ${p.status === 'published' ? 'publié' : 'brouillon'}`}>
                            {p.status === 'published' ? 'Publié' : 'Brouillon'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button className="action-btn view" type="button" title="Voir" aria-label="Voir" onClick={() => setPreviewPost(p)}>
                            <Eye size={16} aria-hidden="true" />
                          </button>
                          <button className="action-btn edit" type="button" title="Modifier" aria-label="Modifier" onClick={() => handleOpenEdit(p)}>
                            <Pencil size={16} aria-hidden="true" />
                          </button>
                          <button
                            className="action-btn"
                            type="button"
                            title={p.status === 'published' ? 'Passer en brouillon' : 'Publier'}
                            aria-label={p.status === 'published' ? 'Passer en brouillon' : 'Publier'}
                            onClick={() => toggleStatus(p)}
                          >
                            {p.status === 'published' ? <Pause size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
                          </button>
                          <button className="action-btn delete" title="Supprimer" aria-label="Supprimer" type="button" onClick={() => requestDeletePost(p._id)}>
                            <Trash2 size={16} aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  {!loading && posts.length === 0 && (
                    <tr>
                      <td colSpan="5" className="table-cell-center">Aucun article</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="news-mobile-cards articles-cards--mobile" role="list" aria-label="Articles">
              {!loading && posts
                .filter(p =>
                  (statusFilter === 'all' || p.status === statusFilter) &&
                  (searchQuery.trim() === '' || (p.title || '').toLowerCase().includes(searchQuery.toLowerCase()) || (p.content || '').toLowerCase().includes(searchQuery.toLowerCase()))
                )
                .map((p) => (
                  <div className="news-mobile-card" key={p._id} role="listitem">
                    <div className="news-mobile-card__top">
                      <div className="news-mobile-card__title">{p.title}</div>
                      <div className="news-mobile-card__meta">
                        <span className={`status-badge ${p.status === 'published' ? 'publié' : 'brouillon'}`}>
                          {p.status === 'published' ? 'Publié' : 'Brouillon'}
                        </span>
                      </div>
                    </div>
                    <div className="news-mobile-card__grid">
                      <div className="news-mobile-card__field">
                        <div className="news-mobile-card__label">Auteur</div>
                        <div className="news-mobile-card__value">{p.author?.name || '—'}</div>
                      </div>
                      <div className="news-mobile-card__field">
                        <div className="news-mobile-card__label">Création</div>
                        <div className="news-mobile-card__value">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('fr-FR') : '—'}</div>
                      </div>
                    </div>
                    <div className="news-mobile-card__actions" aria-label="Actions">
                      <button className="media-action-btn" type="button" onClick={() => setPreviewPost(p)}>
                        <Eye size={16} aria-hidden="true" />
                        Voir
                      </button>
                      <button className="media-action-btn" type="button" onClick={() => handleOpenEdit(p)}>
                        <Pencil size={16} aria-hidden="true" />
                        Modifier
                      </button>
                      <button
                        className={p.status === 'published' ? 'media-action-btn is-reject' : 'media-action-btn is-approve'}
                        type="button"
                        onClick={() => toggleStatus(p)}
                      >
                        {p.status === 'published' ? (
                          <>
                            <Pause size={16} aria-hidden="true" />
                            Brouillon
                          </>
                        ) : (
                          <>
                            <Check size={16} aria-hidden="true" />
                            Publier
                          </>
                        )}
                      </button>
                      <button className="media-action-btn is-danger" type="button" onClick={() => requestDeletePost(p._id)}>
                        <Trash2 size={16} aria-hidden="true" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}

              {!loading && posts.length === 0 && (
                <div className="news-mobile-cards__empty news-page__empty">Aucun article</div>
              )}

              {loading && (
                <div className="news-mobile-cards__empty news-page__empty">Chargement…</div>
              )}
            </div>

            <div className="pagination" aria-label="Pagination">
              <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>{'<'}</button>
              <span className="pagination__meta">Page {currentPage} / {totalPages}</span>
              <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>{'>'}</button>
            </div>
          </div>
          )}

        {confirmOpen && (
          <div className="modal-overlay" onMouseDown={(e) => handleOverlayMouseDown(e, closeConfirm)}>
            <div className="modal">
              <h3>{confirmTitle}</h3>
              <div className="modal-subtitle">{confirmMessage}</div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeConfirm}>Annuler</button>
                <button type="button" className="btn-primary" onClick={handleConfirmSubmit}>Confirmer</button>
              </div>
            </div>
          </div>
        )}

        {replyOpen && (
          <div className="modal-overlay" onMouseDown={(e) => handleOverlayMouseDown(e, closeReplyModal)}>
            <div className="modal">
              <h3>Répondre</h3>
              <form onSubmit={handleSubmitReply}>
                <div className="form-row">
                  <label>Votre réponse</label>
                  <textarea rows="4" value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Écrivez votre réponse…" required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeReplyModal}>Annuler</button>
                  <button type="submit" className="btn-primary">Publier</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );

};

export default AdminNews;
